import React, { useEffect, useRef, useState } from 'react';
import { StatusScreen } from '@mercadopago/sdk-react';
import Modal from 'react-modal'; // Usar react-modal (o personalizar si no quieres usar dependencias)
import { finalizarPedido } from 'scripts/finalizarPedido';
import { consultarEstadoPedido } from 'scripts/consultarEstadoPedido';
import { limpiarCarrito } from 'store/carritoSlice';

const CheckoutStatusModal = ({
	payment,
	isModalOpen,
	setIsModalOpen,
	token,
	pedidoId,
	direccionId,
	fechaEnvio,
	deliveryMethod,
	notas,
	notasCocinera,
	cuponId,
	dispatch,
	sucursalId = null,
}) => {
	const [pagoVerificado, setPagoVerificado] = useState(false);
	const [estadoPagoBackend, setEstadoPagoBackend] = useState(null);
	const [errorProcesamiento, setErrorProcesamiento] = useState(null);
	const ultimoPaymentProcesadoRef = useRef(null);

	useEffect(() => {
		if (payment == null || payment === ultimoPaymentProcesadoRef.current) {
			return;
		}
		ultimoPaymentProcesadoRef.current = payment;
		setErrorProcesamiento(null);
		setEstadoPagoBackend(null);
		setPagoVerificado(false);

		finalizarPedido(
			pedidoId,
			2,
			payment,
			direccionId,
			fechaEnvio,
			deliveryMethod,
			notas,
			notasCocinera,
			cuponId,
			token,
			sucursalId,
		)
			.then((data) => {
				setEstadoPagoBackend(data?.estado_pago || null);
				setPagoVerificado(true);
			})
			.catch(async (e) => {
				// Si el webhook ya procesó el pago, finalizarPedido puede responder error
				// porque el pedido dejó de estar EN_PROGRESO. En ese caso, consultamos
				// el estado actual para mantener el flujo idempotente.
				try {
					const estadoActual = await consultarEstadoPedido(pedidoId, token);
					const estadoPagoActual = estadoActual?.estado_pago || null;
					if (estadoActual?.estado === 2 && (estadoPagoActual === 'APROBADO' || estadoPagoActual === 'PENDIENTE')) {
						setEstadoPagoBackend(estadoPagoActual);
						setErrorProcesamiento(null);
						setPagoVerificado(true);
						return;
					}
				} catch (estadoError) {
					// Si también falla la consulta de estado, se conserva el error original.
				}

				setErrorProcesamiento(e.message || 'No se pudo confirmar el estado del pago.');
				setPagoVerificado(true);
			});
	}, [payment, pedidoId, direccionId, fechaEnvio, deliveryMethod, notas, notasCocinera, cuponId, token, sucursalId]);

	const handleCloseModal = () => {
		setIsModalOpen(false);
		const checkoutFinalizado =
			pagoVerificado && (estadoPagoBackend === 'APROBADO' || estadoPagoBackend === 'PENDIENTE');
		if (checkoutFinalizado) {
			dispatch(limpiarCarrito());
			window.location.href = '/mis_pedidos';
		}
	};

	if (!isModalOpen) {
		return null;
	}

	const renderEstadoTexto = () => {
		if (errorProcesamiento) {
			return 'No se pudo confirmar el pedido. Tu carrito se mantuvo para reintentar.';
		}
		if (estadoPagoBackend === 'APROBADO') {
			return 'Pago aprobado. Tu pedido fue confirmado y el carrito se vaciará al cerrar.';
		}
		if (estadoPagoBackend === 'PENDIENTE') {
			return 'Pago en proceso. Tu carrito se limpió y podés seguir el estado en Mis Pedidos.';
		}
		if (estadoPagoBackend === 'RECHAZADO') {
			return 'Pago rechazado. Tu carrito se mantiene para que puedas intentar nuevamente.';
		}
		return 'Revisando estado del pago...';
	};

	return (
		<div>
			{/* Modal */}
			<Modal
				isOpen={isModalOpen}
				onRequestClose={handleCloseModal}
				contentLabel="Estado del Pago"
				style={{
					content: {
						position: 'fixed',
						top: '50%',
						left: '50%',
						right: 'auto',
						bottom: 'auto',
						marginRight: '-50%',
						transform: 'translate(-50%, -50%)',
						width: '90%',
						maxWidth: '600px',
						padding: '0px 0px 20px 0px',
						borderRadius: '18px',
						backgroundColor: '#fff',
						zIndex: 1002,
						overflowY: 'auto', // Scroll vertical solo si es necesario
						overflowX: 'hidden',
					},
					overlay: {
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(0, 0, 0, 0.75)',
						zIndex: 1001,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					},
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						width: '100%',
						minHeight: 'fit-content',
					}}
				>
					{/* StatusScreen de Mercado Pago */}
					{pagoVerificado ? (
						<div style={{ width: '100%', marginBottom: '20px' }}>
							<StatusScreen initialization={{ paymentId: payment }} />
						</div>
					) : (
						<p style={{ fontSize: '16px', color: '#666', padding: '20px' }}>Cargando estado del pago...</p>
					)}
					<p style={{ fontSize: '14px', color: '#444', padding: '0 20px', textAlign: 'center' }}>{renderEstadoTexto()}</p>

					{/* Botón de Cerrar */}
					<button
						onClick={() => handleCloseModal()}
						style={{
							marginTop: '10px',
							padding: '12px 30px',
							fontSize: '16px',
							fontWeight: '600',
							backgroundColor: '#99cdb6',
							color: '#fff',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							transition: 'background-color 0.2s ease',
							boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
						}}
						onMouseOver={(e) => (e.target.style.backgroundColor = '#7fb89d')}
						onMouseOut={(e) => (e.target.style.backgroundColor = '#99cdb6')}
					>
						Cerrar
					</button>
				</div>
			</Modal>
		</div>
	);
};

export default CheckoutStatusModal;
